import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Loading, StatCard, SectionTitle, GradeBadge, scoreTone, fmtDate, EmptyState } from "@/components/common";
import { WeeklyChart } from "@/pages/StudentDashboard";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Star, Flame, BookCheck, Target, MessageSquare, Unlink, ScrollText } from "lucide-react";
import { toast } from "sonner";

export default function ChildDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/parent/children/${id}`).then(({ data }) => setD(data)).catch((e) => { toast.error(errMsg(e)); navigate("/dashboard"); });
  }, [id, navigate]);
  if (!d) return <Loading />;
  const u = d.user;

  const unlink = async () => {
    if (!window.confirm(`Unlink ${u.name}? You'll stop seeing their progress.`)) return;
    try { await api.delete(`/parent/children/${id}`); toast.success("Unlinked"); navigate("/dashboard"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <Button variant="ghost" asChild className="mb-4 -ml-3"><Link to="/dashboard"><ArrowLeft className="h-4 w-4 mr-1" />My children</Link></Button>
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <div className="h-14 w-14 rounded-full bg-[#E9E1CF] font-display font-semibold text-2xl flex items-center justify-center">{u.name.slice(0, 1)}</div>
        <div>
          <h1 className="text-3xl font-display font-semibold" data-testid="child-name">{u.name}</h1>
          <GradeBadge grade={u.grade_band} />
        </div>
        <Button variant="outline" className="ml-auto bg-white" asChild data-testid="child-records-button"><Link to={`/records/${u.user_id}`}><ScrollText className="h-4 w-4 mr-2" />Transcript & attendance</Link></Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={unlink} data-testid="child-unlink-button"><Unlink className="h-4 w-4 mr-2" />Unlink</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Star} label="XP earned" value={u.xp} tone="amber" testid="child-stat-xp" />
        <StatCard icon={Flame} label="Day streak" value={u.streak} tone="coral" />
        <StatCard icon={BookCheck} label="Lessons done" value={d.lessons_completed} tone="green" testid="child-stat-lessons" />
        <StatCard icon={Target} label="Avg. quiz score" value={d.average_score != null ? `${d.average_score}%` : "—"} hint={`${d.quizzes_taken} quizzes taken`} tone="sky" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-7 bg-white border border-border rounded-[14px] p-5">
          <SectionTitle>Activity this week</SectionTitle>
          <WeeklyChart data={d.weekly} />
        </div>
        <div className="lg:col-span-5 bg-white border border-border rounded-[14px] p-5">
          <SectionTitle>Scores by subject</SectionTitle>
          {d.by_subject.length === 0 ? <p className="text-sm text-muted-foreground">No quizzes yet.</p> : (
            <div className="space-y-4" data-testid="child-subject-scores">
              {d.by_subject.map((s) => (
                <div key={s.subject}>
                  <div className="flex justify-between text-sm mb-1"><span>{s.subject} <span className="text-muted-foreground">({s.count})</span></span><span className={`font-semibold ${scoreTone(s.average)}`}>{s.average}%</span></div>
                  <Progress value={s.average} className="h-2" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-7 bg-white border border-border rounded-[14px] overflow-hidden">
          <div className="p-5 pb-0"><SectionTitle>Recent quiz scores</SectionTitle></div>
          {d.recent_attempts.length === 0 ? <p className="text-sm text-muted-foreground px-5 pb-5">No quizzes taken yet.</p> : (
            <Table data-testid="parent-recent-scores-table">
              <TableHeader><TableRow><TableHead>Lesson</TableHead><TableHead>Subject</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Score</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.recent_attempts.map((a) => (
                  <TableRow key={a.attempt_id}>
                    <TableCell className="font-medium">{a.lesson_title}</TableCell>
                    <TableCell>{a.subject}</TableCell>
                    <TableCell className="text-muted-foreground">{fmtDate(a.created_at)}</TableCell>
                    <TableCell className={`text-right font-display font-semibold ${scoreTone(a.percent)}`}>{a.percent}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-border rounded-[14px] p-5">
            <SectionTitle>Assignments</SectionTitle>
            {d.assignments.length === 0 ? <p className="text-sm text-muted-foreground">No assignments.</p> : (
              <ul className="divide-y divide-border">
                {d.assignments.map((a) => (
                  <li key={a.assignment_id} className="py-2.5 flex justify-between gap-2 text-sm">
                    <div className="min-w-0"><div className="truncate font-medium">{a.lesson_title}</div><div className="text-xs text-muted-foreground">{a.class_name}{a.due_date ? ` · due ${fmtDate(a.due_date)}` : ""}</div></div>
                    <span className={a.completed ? "text-[#14663A] font-medium" : "text-muted-foreground"}>{a.completed ? `Done${a.best_percent != null ? ` · ${a.best_percent}%` : ""}` : "To do"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-white border border-border rounded-[14px] p-5">
            <SectionTitle>Recent tutoring topics</SectionTitle>
            {d.tutor_session_list.length === 0 ? <EmptyState icon={MessageSquare} title="No tutoring yet" /> : (
              <ul className="space-y-2" data-testid="child-tutor-sessions">
                {d.tutor_session_list.map((s) => (
                  <li key={s.session_id} className="flex items-center gap-2 text-sm"><MessageSquare className="h-4 w-4 text-[#0F7B7D] shrink-0" /><span className="truncate flex-1">{s.title}</span><span className="text-xs text-muted-foreground">{fmtDate(s.updated_at)}</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
